/*
 * Copyright © 2019 Franck Borel, Martin Helfer, Sebastian Schüpbach
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 *
 */

import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import {
  DocFieldEdit,
  DocFieldValue,
  FacetSelection,
  FieldCase,
  FieldType,
  LocalizedLabel,
  LocalizedValue,
  ValueCase,
} from '../../shared/models/doc-details.model';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../shared/Environment';
import { DateFormatService } from '../../shared/services/date-format.service';
import * as fromFormActions from '../../search/actions/form.actions';
import { Store } from '@ngrx/store';
import * as fromSearch from '../../search/reducers/index';
import { ResetUtilService } from '../../shared/services/reset-util.service';
import * as fromQueryActions from '../../search/actions/query.actions';
import { defaultViewer } from '../../shared/models/settings-util';
import { FacetFieldModel } from '../../shared/models/settings.model';
import { Router } from '@angular/router';
import { UrlParamsService } from '../../shared/services/url-params.service';

/**
 * Displays a row in the result or  list
 */
@Component({
  selector: 'app-detailed-field',
  template: `
    <ng-template #simpleValueTemplate let-value="value">
      <ng-container [ngSwitch]="calculateValueView(meta.data_type)">
        <ng-container *ngSwitchCase="ValueCaseInTpl.DATE">{{ formatDay(value) }}</ng-container>
        <ng-container *ngSwitchCase="ValueCaseInTpl.STRING_SHORT"><span [innerHTML]="value | safeHtml"></span></ng-container>
        <ng-container *ngSwitchCase="ValueCaseInTpl.BOOL">{{ yesNoLabel(value) | translate }}</ng-container>
        <div *ngSwitchCase="ValueCaseInTpl.STRING_LONG" style="text-align: justify;" [innerHTML]="value | safeHtml"></div>
        <ng-container *ngSwitchDefault><span [innerHTML]="value | safeHtml"></span></ng-container>
      </ng-container>
    </ng-template>
    <ng-template #docValueTemplate let-value="value">
      <ng-container [ngSwitch]="calculateFieldView(value)">
        <ng-container *ngSwitchCase="FieldCaseInTpl.OBJ_LABEL">
          <ng-container [ngTemplateOutlet]="simpleValueTemplate" [ngTemplateOutletContext]="{ value: value.label }"></ng-container>
        </ng-container>
        <ng-container *ngSwitchCase="FieldCaseInTpl.OBJ_LINK">
          <a
            *ngIf="isExternalLink(value.link)"
            target="_blank"
            class="link-black detailed-field__external-link"
            [href]="value.link"
            [innerHTML]="value.label | safeHtml"
          ></a>
          <a
            *ngIf="isInternalLink(value.link)"
            class="link-black detailed-field__show-object"
            [href]="enhanceUrl(value.link)"
            title="{{ 'detailed.go_to_object' | translate }}"
            [innerHTML]="value.label | safeHtml"
          ></a>
        </ng-container>
        <ng-container *ngSwitchCase="FieldCaseInTpl.OBJ_FACET">
          <button
            class="btn btn-black detailed-field__start-search"
            (click)="showSearchPageWith(value.selection)"
            title="{{ 'detailed.seach_prefix' | translate }}{{ value.label }}"
            [innerHTML]="value.label | safeHtml"
          ></button>
        </ng-container>
        <ng-container *ngSwitchDefault>
          <ng-container [ngTemplateOutlet]="simpleValueTemplate" [ngTemplateOutletContext]="{ value: value }"></ng-container>
        </ng-container>
      </ng-container>
    </ng-template>
    <div class="row detailed-field" *ngIf="showField()">
      <div class="detailed-field__label col-12 col-sm-3">{{ getLabel() }}:</div>
      <div class="detailed-field__value col-12 col-sm-9">
        <ng-container *ngIf="getValue(); let resolvedValue">
          <div *ngIf="meta && (!meta.repeatable || (resolvedValue && resolvedValue.length === 1))">
            <ng-container
              [ngTemplateOutlet]="docValueTemplate"
              [ngTemplateOutletContext]="{ value: resolvedValue ? resolvedValue[0] : undefined }"
            ></ng-container>
          </div>
          <ul *ngIf="meta && meta.repeatable && resolvedValue && resolvedValue.length > 1">
            <li *ngFor="let value of resolvedValue">
              <ng-container [ngTemplateOutlet]="docValueTemplate" [ngTemplateOutletContext]="{ value: value }"></ng-container>
            </li>
          </ul>
          <ul *ngIf="!meta && resolvedValue && resolvedValue.length > 0">
            <li *ngFor="let value of resolvedValue">
              <ng-container [ngTemplateOutlet]="docValueTemplate" [ngTemplateOutletContext]="{ value: value }"></ng-container>
            </li>
          </ul>
        </ng-container>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DetailedFieldComponent {
  @Input() fieldId: string;
  @Input() label: string | LocalizedLabel;
  @Input() value: string | number | boolean | LocalizedValue;
  @Input() meta: DocFieldEdit;

  FieldCaseInTpl = FieldCase;
  ValueCaseInTpl = ValueCase;

  constructor(
    protected translate: TranslateService,
    protected dateFormatService: DateFormatService,
    protected searchStore: Store<fromSearch.State>,
    protected resetUtilService: ResetUtilService,
    protected router: Router
  ) {}

  enhanceUrl(s: string): string {
    return UrlParamsService.enhanceUrl(s);
  }

  calculateFieldView(value: number | string | DocFieldValue): FieldCase {
    if (this.valueIsString(value)) {
      return FieldCase.STRING;
    }
    if (this.valueIsNumber(value)) {
      return FieldCase.NUMBER;
    }
    if (this.valueIsObj(value)) {
      const vo = value as DocFieldValue;
      if (vo.link) {
        return FieldCase.OBJ_LINK;
      }
      if (vo.selection) {
        const fields = Object.keys(vo.selection);
        const configuredFields = fields.filter((f) => !!this.facetConfigByField(f));
        if (configuredFields.length > 0) {
          return FieldCase.OBJ_FACET;
        } else {
          return FieldCase.OBJ_LABEL;
        }
      }
      if (vo.label) {
        return FieldCase.OBJ_LABEL;
      }
    }
    return FieldCase.UNKNOWN;
  }

  protected facetConfigByField(field: string): FacetFieldModel | undefined {
    const keys = Object.keys(environment.facetFields);
    for (const key of keys) {
      if (environment.facetFields[key]?.field === field) {
        return environment.facetFields[key];
      }
    }
    return undefined;
  }

  calculateValueView(type: FieldType): ValueCase {
    if (type === 'date') {
      return ValueCase.DATE;
    }
    if (type === 'str') {
      return ValueCase.STRING_SHORT;
    }
    if (type === 'string_long') {
      return ValueCase.STRING_LONG;
    }
    if (type === 'boolean') {
      return ValueCase.BOOL;
    }
    return ValueCase.OTHER;
  }

  isInternalLink(v: string): boolean {
    return !this.isExternalLink(v);
  }

  isExternalLink(v: string): boolean {
    if (Array.isArray(v)) {
      if (v.length === 0) {
        return false;
      }
      v = v[0];
    }
    return (v ?? '').trim().startsWith('http');
  }

  formatDay(isoDate: string): string {
    try {
      return this.dateFormatService.formatLabel({ value: { gte: isoDate, type: 'day' } }).label;
    } catch (ex) {
      console.error('Failed to parse iso date: <' + isoDate + '>', ex);
      return isoDate;
    }
  }

  showField(): boolean {
    const value = this.getValue();
    return !this.meta || this.meta.show_empty || (!this.meta.show_empty && value !== undefined && value.length > 0);
  }

  getLabel(): string {
    let l: string = undefined;
    const labelType = typeof this.label;
    if (labelType === 'string') {
      l = this.label as string;
    } else if (labelType === 'object') {
      const currentLang = this.translate.currentLang;
      const labelObj = this.label as LocalizedLabel;
      let lv = labelObj[environment.defaultLanguage || 'de'];
      if (currentLang in labelObj) {
        lv = labelObj[currentLang];
      }
      if (lv) {
        if (typeof lv === 'string') {
          l = lv as string;
        } else {
          l = lv.join(' ');
        }
      }
    }
    if (!l) {
      l = this.fieldId;
    }
    return l;
  }

  getValue(): (number | string | boolean | DocFieldValue)[] {
    let v: (number | string | boolean | DocFieldValue)[] = [];
    const valueType = typeof this.value;
    if (valueType === 'string') {
      return [this.value as string];
    } else if (valueType === 'boolean') {
      return [this.value as boolean];
    } else if (valueType === 'number') {
      return [this.value as number];
    } else if (valueType === 'object') {
      const currentLang = this.translate.currentLang;
      const valueObj = this.value as LocalizedValue;
      v = valueObj[environment.defaultLanguage || 'de'];
      if (currentLang in valueObj) {
        v = valueObj[currentLang];
      }
    }
    return v;
  }

  valueIsString(v: number | string | DocFieldValue): boolean {
    return typeof v === 'string';
  }

  valueIsNumber(v: number | string | DocFieldValue): boolean {
    return typeof v === 'number';
  }

  valueIsObj(v: number | string | DocFieldValue): boolean {
    return typeof v === 'object';
  }

  showSearchPageWith(facetsToSelect: FacetSelection) {
    this.searchStore.dispatch(new fromQueryActions.SetResultListDisplayMode(defaultViewer()));
    this.resetUtilService.switchToSearchView().then(() => {
      this.searchStore.dispatch(new fromFormActions.SelectFacets(facetsToSelect));
      this.searchStore.dispatch(new fromQueryActions.SimpleSearch());
    });
  }

  yesNoLabel(boolValue): string {
    return boolValue === true ? 'yes' : 'no';
  }
}
